/**
 * In-app cleaning photo capture for claims evidence.
 * - Photos come from getUserMedia + canvas (not the phone library / gallery picker).
 * - Capture metadata (time, GPS, hash, source) is stored for claims.
 */
import { supabase } from "@/integrations/supabase/client";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";

export type CleaningPhotoMoment =
  | "keys"
  | "walk"
  | "after"
  | "drop_keys"
  | "inspect"
  | "issue"
  | "maintenance";

export type CleaningPhotoCaptureMeta = {
  source: "in_app_camera";
  captured_at: string;
  lat: number | null;
  lng: number | null;
  accuracy_m: number | null;
  width: number | null;
  height: number | null;
  content_sha256: string | null;
  user_agent: string;
  platform: string;
};

export type CleaningPhotoCaptureRecord = CleaningPhotoCaptureMeta & {
  url: string;
  moment: CleaningPhotoMoment;
  capture_id?: string;
};

function blobToFile(blob: Blob, name: string): File {
  return new File([blob], name, { type: blob.type || "image/jpeg" });
}

export async function captureFrameFromVideo(
  video: HTMLVideoElement,
  quality = 0.88,
): Promise<{ blob: Blob; width: number; height: number }> {
  if (video.videoWidth < 2 || video.videoHeight < 2) {
    throw new Error("Camera not ready");
  }
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(video, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality),
  );
  if (!blob) throw new Error("Capture failed");
  return { blob, width: canvas.width, height: canvas.height };
}

/** Best-effort GPS at capture time. Short timeout so the shutter never waits on a fix. */
export async function getCaptureLocation(
  timeoutMs = 250,
): Promise<{ lat: number; lng: number; accuracy_m: number | null } | null> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return null;
  if (timeoutMs <= 0) return null;
  try {
    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        maximumAge: 60_000,
        timeout: timeoutMs,
      });
    });
    return {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy_m:
        typeof pos.coords.accuracy === "number" && Number.isFinite(pos.coords.accuracy)
          ? pos.coords.accuracy
          : null,
    };
  } catch {
    return null;
  }
}

export async function hashBlobSha256(blob: Blob): Promise<string | null> {
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return null;
    const buf = await blob.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

export async function buildInAppCaptureMeta(opts: {
  width?: number | null;
  height?: number | null;
  blob?: Blob | null;
  /** Skip GPS when 0. Default stays short so upload is never GPS-bound. */
  locationTimeoutMs?: number;
}): Promise<CleaningPhotoCaptureMeta> {
  const locationTimeoutMs = opts.locationTimeoutMs ?? 250;
  const [loc, content_sha256] = await Promise.all([
    getCaptureLocation(locationTimeoutMs),
    opts.blob ? hashBlobSha256(opts.blob) : Promise.resolve(null),
  ]);
  return {
    source: "in_app_camera",
    captured_at: new Date().toISOString(),
    lat: loc?.lat ?? null,
    lng: loc?.lng ?? null,
    accuracy_m: loc?.accuracy_m ?? null,
    width: opts.width ?? null,
    height: opts.height ?? null,
    content_sha256,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : "",
    platform: typeof navigator !== "undefined" ? navigator.platform || "" : "",
  };
}

export async function uploadInAppCleaningPhoto(opts: {
  propertyId: string;
  taskId: string;
  moment: CleaningPhotoMoment;
  blob: Blob;
  width?: number | null;
  height?: number | null;
  /** Storage path under cleaning-photos bucket (no leading slash). */
  storagePath: string;
  locationTimeoutMs?: number;
}): Promise<{ url: string; captureId: string | null; meta: CleaningPhotoCaptureMeta; record: CleaningPhotoCaptureRecord }> {
  const prepared = await prepareImageFileForUpload(
    blobToFile(opts.blob, `${opts.moment}_${Date.now()}.jpg`),
  );

  // Upload first so the shutter path is not blocked on hash/GPS.
  const { error } = await supabase.storage.from("cleaning-photos").upload(opts.storagePath, prepared, {
    contentType: "image/jpeg",
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("cleaning-photos").getPublicUrl(opts.storagePath);
  const url = data.publicUrl;

  const meta = await buildInAppCaptureMeta({
    width: opts.width,
    height: opts.height,
    blob: prepared,
    locationTimeoutMs: opts.locationTimeoutMs ?? 0,
  });

  let captureId: string | null = null;
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id ?? null;
  if (userId) {
    // Table added in migration 20260916300000 — cast until types regenerate.
    const captures = supabase.from("cleaning_photo_captures" as never);
    const { data: row, error: insertError } = await (captures as ReturnType<typeof supabase.from>)
      .insert({
        property_id: opts.propertyId,
        cleaning_task_id: opts.taskId,
        captured_by: userId,
        photo_url: url,
        storage_path: opts.storagePath,
        moment: opts.moment,
        source: "in_app_camera",
        captured_at: meta.captured_at,
        lat: meta.lat,
        lng: meta.lng,
        accuracy_m: meta.accuracy_m,
        width: meta.width,
        height: meta.height,
        content_sha256: meta.content_sha256,
        user_agent: meta.user_agent,
        platform: meta.platform,
        metadata: {
          source: meta.source,
          moment: opts.moment,
        },
      } as never)
      .select("id")
      .maybeSingle();
    if (!insertError && row && typeof row === "object" && "id" in row) {
      captureId = String((row as { id: string }).id);
    }
    // Non-fatal if metadata insert fails — photo is already stored.
    if (insertError) console.warn("[cleaningPhotoCapture] metadata insert failed", insertError.message);
  }

  const record: CleaningPhotoCaptureRecord = {
    ...meta,
    url,
    moment: opts.moment,
    capture_id: captureId ?? undefined,
  };
  return { url, captureId, meta, record };
}

export function appendPhotoCaptureRecord(
  existing: CleaningPhotoCaptureRecord[] | undefined,
  record: CleaningPhotoCaptureRecord,
): CleaningPhotoCaptureRecord[] {
  return [...(existing ?? []), record];
}
